import React, { useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity,
  ActivityIndicator, Image,
} from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import Toast from 'react-native-toast-message';
import { useFocusEffect } from '@react-navigation/native';
import { getBlockedProfiles, getProfileById, unblockProfile } from '../services/api';
import { THEME } from '../components';
import type { Profile } from '../types';

const toHttps = (url?: string) => url && !url.startsWith('http') ? `https://${url}` : url;

const BlockedProfilesScreen: React.FC = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      setLoading(true);
      getBlockedProfiles()
        .then(async res => {
          const ids: string[] = Array.isArray(res.data) ? res.data : [];
          if (ids.length === 0) { setProfiles([]); return; }
          const results = await Promise.all(
            ids.map(id => getProfileById(id).then(r => r.data).catch(() => null))
          );
          setProfiles(results.filter(Boolean) as Profile[]);
        })
        .catch(() => Toast.show({ type: 'error', text1: 'Failed to load blocked profiles' }))
        .finally(() => setLoading(false));
    }, []),
  );

  const handleUnblock = async (profileId: string) => {
    setUnblocking(profileId);
    try {
      await unblockProfile(profileId);
      setProfiles(prev => prev.filter(p => p.id !== profileId));
      Toast.show({ type: 'success', text1: 'Profile unblocked' });
    } catch {
      Toast.show({ type: 'error', text1: 'Could not unblock profile' });
    } finally {
      setUnblocking(null);
    }
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={THEME.primary} />
      </View>
    );
  }

  if (profiles.length === 0) {
    return (
      <View style={styles.center}>
        <Icon name="ban-outline" size={52} color={THEME.border} />
        <Text style={styles.emptyTitle}>No Blocked Profiles</Text>
        <Text style={styles.emptySub}>Profiles you block will appear here.</Text>
      </View>
    );
  }

  return (
    <FlatList
      data={profiles}
      keyExtractor={item => item.id}
      contentContainerStyle={styles.list}
      ItemSeparatorComponent={() => <View style={styles.separator} />}
      renderItem={({ item }) => (
        <View style={styles.row}>
          {item.photoUrl || item.thumbnailUrl
            ? <Image
                style={styles.avatar}
                source={{ uri: toHttps(item.thumbnailUrl ?? item.photoUrl)! }}
                resizeMode="cover"
              />
            : <View style={[styles.avatar, styles.avatarPlaceholder]}>
                <Text style={styles.avatarInitial}>{item.name?.charAt(0)?.toUpperCase() ?? '?'}</Text>
              </View>
          }
          <View style={styles.info}>
            <Text style={styles.name} numberOfLines={1}>{item.name}</Text>
            <Text style={styles.sub} numberOfLines={1}>
              {[item.profileCode, item.district, item.stateName].filter(Boolean).join(' • ')}
            </Text>
          </View>
          <TouchableOpacity
            style={styles.unblockBtn}
            disabled={unblocking === item.id}
            onPress={() => handleUnblock(item.id)}>
            {unblocking === item.id
              ? <ActivityIndicator size="small" color={THEME.primary} />
              : <Text style={styles.unblockText}>Unblock</Text>}
          </TouchableOpacity>
        </View>
      )}
    />
  );
};

export default BlockedProfilesScreen;

const styles = StyleSheet.create({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: THEME.bg },
  emptyTitle: { fontSize: 18, fontWeight: '800', color: THEME.text, marginTop: 16 },
  emptySub: { fontSize: 14, color: THEME.textLight, marginTop: 6 },
  list: { padding: 16, backgroundColor: THEME.bg },
  separator: { height: 1, backgroundColor: THEME.border, marginHorizontal: 4 },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: THEME.card, borderRadius: 14,
    padding: 14, marginBottom: 10,
    shadowColor: '#000', shadowOpacity: 0.05, shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 }, elevation: 2,
  },
  avatar: { width: 52, height: 52, borderRadius: 26 },
  avatarPlaceholder: { backgroundColor: THEME.border, alignItems: 'center', justifyContent: 'center' },
  avatarInitial: { fontSize: 20, fontWeight: '800', color: THEME.textLight },
  info: { flex: 1 },
  name: { fontSize: 16, fontWeight: '700', color: THEME.text },
  sub: { fontSize: 12, color: THEME.textLight, marginTop: 3 },
  unblockBtn: {
    paddingHorizontal: 14, paddingVertical: 7, borderRadius: 8,
    borderWidth: 1.5, borderColor: THEME.primary, backgroundColor: '#FFF0F4',
  },
  unblockText: { fontSize: 13, fontWeight: '700', color: THEME.primary },
});
